/* voice-picker.js — the ONE voice gallery: name · gender · price · ▶ preview.
 *
 * Why this exists
 * ---------------
 * Every tool that offers a catalogue of voices has the same job: let the user
 * HEAR a voice before committing to it. /video-dubbing grew a proper card grid
 * for that; /audio-translator shipped with a bare <select> and threw the preview
 * URL away in a single .map() — even though the server hands it the very same
 * payload, and the sample MP3s are already rendered and served from both
 * origins (openai 13×9, yandex 15, silero 29).
 *
 * What that cost, measured on prod over 30 days (both shards):
 *
 *     /audio-translator — «picked the lane» → «started a run»
 *       free     48 sess →  8   (16.7%)   run it to hear it; costs nothing
 *       clone    69 sess → 10   (14.5%)   it is the speaker's OWN voice
 *       premium  60 sess →  1   ( 1.7%)   the ONLY lane you cannot hear
 *
 * Inside one screen, the lane whose sound is a mystery converts eight times
 * worse than its neighbours. The report that started this (#6274, a Премиум
 * account with 4604 tokens on the balance) chose the free lane and asked to be
 * able to listen to the studio voices.
 *
 * So the gallery becomes a primitive instead of a third copy — the same move
 * the voice CATALOGUE already made when it was consolidated out of seven
 * copies into config/pro.php.
 *
 * Contract
 * --------
 *   var picker = window.VoicePicker.mount(rootEl, {
 *       select:    HTMLSelectElement,   // source of truth the run-flow reads
 *       groups:    [{key, label, voices: [{id, label, gender, rate, preview}]}],
 *       rateText:  function (voice) { return '12 ток/мин'; },   // optional
 *       compare:   function () { return {url, startSec, seconds}; },  // optional
 *       strings:   {preview, compare, compare_stop, original, no_preview},
 *       onChange:  function (id) {},
 *       telemetry: function (name, data) {},
 *   });
 *   picker.setGroups(groups);   picker.value = 'openai:nova';   picker.stop();
 *
 * The hidden <select> stays authoritative: both consumers already read
 * `el.value` deep inside their run-flow, and a grid that only *looked*
 * selected would be a new class of bug.
 */
(function () {
    'use strict';

    /* The loader component defines a stub BEFORE this file so a failed load
     * cannot ReferenceError the whole tool. Replacing it is the normal path —
     * a bare `if (window.VoicePicker) return` would hand every page the stub
     * and no gallery would ever render. */
    if (window.VoicePicker && !window.VoicePicker.__isStub) { return; }

    var CSS_ID = 'vp-styles';

    /* Card metrics live here rather than in each tool's blade so the gallery
     * cannot drift between pages. Every font-size is ≥12px on purpose — below
     * that Google penalises the page on mobile (CLAUDE.md, SEO rules), and the
     * grid this was extracted from had an 11px meta line. */
    var CSS = [
        '.vp-head{display:flex;align-items:center;gap:8px;flex-wrap:wrap;margin-bottom:10px}',
        '.vp-tabs{display:flex;gap:6px;flex-wrap:wrap;flex:1 1 auto}',
        '.vp-tab{background:var(--bg-tertiary,rgba(255,255,255,.04));border:1px solid var(--border);',
        'color:var(--text-secondary);font:600 12px/1 inherit;padding:8px 13px;border-radius:999px;',
        'cursor:pointer;white-space:nowrap;transition:border-color .15s,color .15s}',
        '.vp-tab:hover{border-color:var(--accent)}',
        '.vp-tab.active{background:var(--accent-gradient,linear-gradient(135deg,#38bdf8,#6366f1));',
        'color:var(--on-accent);border-color:transparent}',
        '.vp-orig{flex:none;background:transparent;border:1px solid var(--border);color:var(--text-secondary);',
        'font:600 12px/1 inherit;padding:8px 12px;border-radius:999px;cursor:pointer;white-space:nowrap;',
        'display:inline-flex;align-items:center;gap:6px;transition:border-color .15s,color .15s}',
        '.vp-orig:hover{border-color:var(--accent);color:var(--accent)}',
        '.vp-orig.playing{background:var(--accent);color:var(--on-accent);border-color:transparent}',
        '.vp-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(150px,1fr));gap:8px;',
        'max-height:230px;overflow-y:auto;padding:2px}',
        '.vp-card{display:flex;align-items:center;gap:8px;padding:9px 10px;',
        'background:var(--bg-tertiary,rgba(255,255,255,.03));border:1px solid var(--border);',
        'border-radius:var(--radius-md,10px);cursor:pointer;transition:border-color .12s}',
        '.vp-card:hover{border-color:var(--accent)}',
        '.vp-card.active{border-color:var(--accent);box-shadow:0 0 0 2px rgba(56,189,248,.18)}',
        '.vp-card:focus-visible{outline:2px solid var(--accent);outline-offset:2px}',
        '.vp-card-body{flex:1;min-width:0;display:flex;flex-direction:column;gap:2px}',
        '.vp-card-name{font-size:13px;font-weight:600;color:var(--text-primary);white-space:nowrap;',
        'overflow:hidden;text-overflow:ellipsis}',
        '.vp-card-meta{font-size:12px;color:var(--text-muted)}',
        '.vp-card-play{flex:none;width:28px;height:28px;border-radius:50%;border:1px solid var(--border);',
        'background:transparent;color:var(--text-secondary);cursor:pointer;font-size:12px;',
        'display:inline-flex;align-items:center;justify-content:center}',
        '.vp-card-play:hover{border-color:var(--accent);color:var(--accent)}',
        '.vp-card-play.playing{background:var(--accent);color:var(--on-accent);border-color:transparent}',
        '.vp-card-play[disabled]{opacity:.4;cursor:default}'
    ].join('');

    function injectCss() {
        if (document.getElementById(CSS_ID)) { return; }
        var s = document.createElement('style');
        s.id = CSS_ID;
        s.textContent = CSS;
        (document.head || document.documentElement).appendChild(s);
    }

    function glyphFor(gender) {
        var g = String(gender || '').toLowerCase().charAt(0);
        if (g === 'f' || g === 'ж') { return ' ♀'; }
        if (g === 'm' || g === 'м') { return ' ♂'; }
        return '';
    }

    /* ── the preview engine ───────────────────────────────────────────────
     *
     * Previews play through Web Audio (decode → BufferSource), NOT an <audio>
     * element. A played media element hijacks the OS "Now Playing" widget with
     * the bare page title and LINGERS there after it ends — so merely browsing
     * voices (nothing uploaded, nothing processing) looked like the tab was
     * kept alive in the background. Web Audio never registers with the media
     * session. The <audio> fallback below only runs when Web Audio is
     * unavailable or the file cannot be fetched/decoded.
     *
     * ONE engine for the whole page: two pickers (a free lane and a paid lane
     * on the same screen) must not talk over each other.
     */
    var Engine = {
        ctx: null,
        src: null,
        el: null,
        bufs: Object.create(null),
        btn: null,
        onStop: null
    };

    function engineStop() {
        try {
            if (Engine.src) {
                clearTimeout(Engine.src.__guard);
                Engine.src.onended = null;
                Engine.src.stop(0);
            }
        } catch (e) { /* already stopped */ }
        Engine.src = null;
        try { if (Engine.el) { Engine.el.pause(); } } catch (e) { /* not playing */ }
        if (Engine.btn) {
            Engine.btn.classList.remove('playing');
            Engine.btn = null;
        }
        if (Engine.onStop) {
            var fn = Engine.onStop;
            Engine.onStop = null;
            try { fn(); } catch (e) { /* a listener must not wedge the engine */ }
        }
    }

    function fallbackPlay(url, btn) {
        try {
            if (!Engine.el) {
                Engine.el = new Audio();
                try { Engine.el.playsInline = true; } catch (e) { /* not iOS */ }
                Engine.el.addEventListener('ended', function () {
                    if (Engine.btn) { engineStop(); }
                });
                if (window.__audioGate && window.__audioGate.arm) { window.__audioGate.arm(Engine.el); }
            }
            Engine.el.src = url;
            var p = Engine.el.play();
            if (p && p['catch']) {
                p['catch'](function () { if (Engine.btn === btn) { engineStop(); } });
            }
        } catch (e) {
            if (Engine.btn === btn) { engineStop(); }
        }
    }

    /**
     * Play `url` and light `btn` for its duration.
     * MUST be called from inside the click handler: ctx.resume() below is what
     * spends the user activation, and any await before it loses the gesture.
     */
    function enginePlay(url, btn, onStop) {
        try {
            engineStop();
            Engine.btn = btn || null;
            Engine.onStop = onStop || null;
            if (btn) { btn.classList.add('playing'); }

            if (!Engine.ctx) {
                try {
                    Engine.ctx = new (window.AudioContext || window.webkitAudioContext)();
                } catch (e) { Engine.ctx = null; }
            }
            if (!Engine.ctx) { fallbackPlay(url, btn); return; }

            /* resume() UNCONDITIONALLY — not only when state === 'suspended'. A
             * context can report 'running' while its clock is NOT advancing
             * (measured: a fresh context in headless Chromium sits at
             * currentTime 0 forever), and iOS parks contexts as 'interrupted'
             * after a call or Siri. Without this the buffer never plays,
             * onended never fires, and the ▶ stays lit forever. */
            try { Engine.ctx.resume(); } catch (e) { /* nothing to resume */ }

            var start = function (buf) {
                if (Engine.btn !== btn) { return; }   // a newer click superseded this one
                var src = Engine.ctx.createBufferSource();
                src.buffer = buf;
                src.connect(Engine.ctx.destination);
                src.onended = function () { if (Engine.src === src) { engineStop(); } };
                /* Watchdog: if the context is interrupted mid-clip `onended`
                 * never arrives — clear the button anyway instead of leaving it
                 * stuck lit. */
                src.__guard = setTimeout(function () {
                    if (Engine.src === src) { engineStop(); }
                }, Math.ceil((buf.duration || 1) * 1000) + 1500);
                Engine.src = src;
                src.start(0);
            };

            if (Engine.bufs[url]) { start(Engine.bufs[url]); return; }

            fetch(url, { credentials: 'same-origin' })
                .then(function (r) { if (!r.ok) { throw new Error('preview_http_' + r.status); } return r.arrayBuffer(); })
                .then(function (ab) { return Engine.ctx.decodeAudioData(ab); })
                .then(function (buf) { Engine.bufs[url] = buf; start(buf); })
                ['catch'](function () { if (Engine.btn === btn) { fallbackPlay(url, btn); } });
        } catch (e) {
            if (btn) { btn.classList.remove('playing'); }
        }
    }

    /* ── the original-audio A/B player ────────────────────────────────────
     *
     * "Does this voice suit MY speaker?" is the question a canned sample cannot
     * answer, and it is the one the user actually has. Both recordings are
     * already on the page, so answering it costs nothing.
     *
     * The source file goes through an <audio> element rather than Web Audio on
     * purpose: it can be a 200 MB video, and decoding it whole to an AudioBuffer
     * to hear five seconds would be the exact mistake WaveformPreview exists to
     * prevent. Seek + stop on timeupdate is the pattern /video-dubbing already
     * uses for result segments.
     */
    var Orig = { el: null, stop: null, btn: null };

    function origStop() {
        if (Orig.stop) {
            var fn = Orig.stop;
            Orig.stop = null;
            try { fn(); } catch (e) { /* detach must not throw */ }
        }
        try { if (Orig.el) { Orig.el.pause(); } } catch (e) { /* not playing */ }
        if (Orig.btn) {
            Orig.btn.classList.remove('playing');
            Orig.btn = null;
        }
    }

    function origPlay(spec, btn) {
        origStop();
        engineStop();                      // never two sounds at once
        if (!spec || !spec.url) { return; }

        if (!Orig.el) {
            Orig.el = new Audio();
            try { Orig.el.playsInline = true; } catch (e) { /* not iOS */ }
            if (window.__audioGate && window.__audioGate.arm) { window.__audioGate.arm(Orig.el); }
        }
        Orig.btn = btn || null;
        if (btn) { btn.classList.add('playing'); }

        var from = Math.max(0, Number(spec.startSec) || 0);
        var until = from + (Number(spec.seconds) || 5);

        var tick = function () {
            if (Orig.el.currentTime >= until) { origStop(); }
        };
        var detach = function () {
            Orig.el.removeEventListener('timeupdate', tick);
            Orig.el.removeEventListener('ended', origStop);
        };
        Orig.stop = detach;

        var begin = function () {
            try { Orig.el.currentTime = from; } catch (e) { /* seek before metadata */ }
            Orig.el.addEventListener('timeupdate', tick);
            Orig.el.addEventListener('ended', origStop);
            /* play() directly, NOT __audioGate.play(): the gate resets
             * currentTime to 0 for an element that already has one, which is
             * exactly the seek we just made — the comparison would always start
             * at the top of the file instead of at the first thing the speaker
             * says. The element is arm()ed above, so the gate has still unlocked
             * it on this very click (its gesture listener runs in the capture
             * phase); what we skip here is only its rewind. */
            var p;
            try { p = Orig.el.play(); } catch (e) { origStop(); return; }
            if (p && p['catch']) { p['catch'](function () { origStop(); }); }
        };

        if (Orig.el.src !== spec.url) {
            Orig.el.src = spec.url;
            /* Seeking before metadata silently lands at 0 — wait for the header,
             * which for a local blob URL is immediate. */
            Orig.el.addEventListener('loadedmetadata', begin, { once: true });
            try { Orig.el.load(); } catch (e) { /* some engines auto-load */ }
        } else {
            begin();
        }
    }

    /* ── the picker ───────────────────────────────────────────────────────── */

    function Picker(root, opts) {
        this.root = root;
        this.opts = opts || {};
        this.select = this.opts.select || null;
        this.groups = [];
        this.tab = null;
        this.strings = this.opts.strings || {};

        injectCss();
        root.innerHTML = '';

        this.headEl = document.createElement('div');
        this.headEl.className = 'vp-head';
        this.tabsEl = document.createElement('div');
        this.tabsEl.className = 'vp-tabs';
        this.headEl.appendChild(this.tabsEl);

        this.gridEl = document.createElement('div');
        this.gridEl.className = 'vp-grid';
        this.gridEl.setAttribute('role', 'radiogroup');
        if (this.opts.label) { this.gridEl.setAttribute('aria-label', this.opts.label); }

        root.appendChild(this.headEl);
        root.appendChild(this.gridEl);

        this.setGroups(this.opts.groups || []);
    }

    Picker.prototype.tel = function (name, data) {
        if (typeof this.opts.telemetry === 'function') {
            try { this.opts.telemetry(name, data || {}); } catch (e) { /* telemetry is advisory */ }
        }
    };

    Picker.prototype.allVoices = function () {
        var out = [];
        this.groups.forEach(function (g) {
            (g.voices || []).forEach(function (v) { out.push(v); });
        });
        return out;
    };

    Picker.prototype.groupOf = function (id) {
        for (var i = 0; i < this.groups.length; i++) {
            var vs = this.groups[i].voices || [];
            for (var j = 0; j < vs.length; j++) { if (vs[j].id === id) { return this.groups[i].key; } }
        }
        return null;
    };

    /**
     * Replace the catalogue. Keeps the current voice selected when it survives
     * the new list — switching the target language must not silently move the
     * user to a different voice than the one the summary line names.
     */
    Picker.prototype.setGroups = function (groups) {
        this.groups = (groups || []).filter(function (g) { return g && (g.voices || []).length; });

        if (this.select) {
            var prev = this.select.value;
            var keep = null;
            this.select.innerHTML = '';
            this.groups.forEach(function (g) {
                (g.voices || []).forEach(function (v) {
                    var o = document.createElement('option');
                    o.value = v.id;
                    o.textContent = v.label;
                    /* The option carries the whole catalogue entry as data. It
                     * is not decoration: consumers price the job by reading
                     * `data-rate` off the SELECTED option, so a grid that knew
                     * the rate while the select did not would quietly bill from
                     * a stale number. */
                    o.setAttribute('data-group', g.key);
                    o.setAttribute('data-label', v.label);
                    if (v.gender) { o.setAttribute('data-gender', v.gender); }
                    if (v.preview) { o.setAttribute('data-preview', v.preview); }
                    if (v.rate !== undefined && v.rate !== null) { o.setAttribute('data-rate', v.rate); }
                    if (v.id === prev) { o.selected = true; keep = v.id; }
                    this.select.appendChild(o);
                }, this);
            }, this);
            if (!keep && this.select.options.length) { this.select.selectedIndex = 0; }
        }

        var cur = this.value;
        var curGroup = this.groupOf(cur);
        if (!this.tab || !this.groups.some(function (g) { return g.key === this.tab; }, this)) {
            this.tab = curGroup || (this.groups[0] && this.groups[0].key) || null;
        }
        this.render();
    };

    Picker.prototype.render = function () {
        var self = this;
        var cur = this.value;

        /* tabs — only when there is something to switch between */
        this.tabsEl.innerHTML = '';
        if (this.groups.length > 1) {
            this.groups.forEach(function (g) {
                var b = document.createElement('button');
                b.type = 'button';
                b.className = 'vp-tab' + (g.key === self.tab ? ' active' : '');
                b.textContent = g.label || g.key;
                b.setAttribute('aria-pressed', g.key === self.tab ? 'true' : 'false');
                b.addEventListener('click', function () {
                    self.tab = g.key;
                    self.render();
                    self.tel('voice_tab', { group: g.key });
                });
                self.tabsEl.appendChild(b);
            });
        }

        /* the A/B button — present only while the caller can actually supply
         * source audio. A dead "compare" is worse than none. */
        if (this.origBtn) { this.origBtn.remove(); this.origBtn = null; }
        if (typeof this.opts.compare === 'function' && this.opts.compare()) {
            var ob = document.createElement('button');
            ob.type = 'button';
            ob.className = 'vp-orig';
            ob.textContent = '▶ ' + (this.strings.original || 'Original');
            ob.title = this.strings.compare || '';
            ob.addEventListener('click', function () {
                if (Orig.btn === ob) { origStop(); return; }
                var spec = self.opts.compare();
                if (!spec) { return; }
                origPlay(spec, ob);
                self.tel('voice_compare_original', {});
            });
            this.headEl.appendChild(ob);
            this.origBtn = ob;
        }

        /* the grid of the active group */
        this.gridEl.innerHTML = '';
        var grp = null;
        for (var i = 0; i < this.groups.length; i++) {
            if (this.groups[i].key === this.tab) { grp = this.groups[i]; break; }
        }
        if (!grp) { return; }

        grp.voices.forEach(function (v) {
            var card = document.createElement('div');
            card.className = 'vp-card' + (v.id === cur ? ' active' : '');
            card.setAttribute('role', 'radio');
            card.setAttribute('aria-checked', v.id === cur ? 'true' : 'false');
            /* Only the checked card is tabbable: a radiogroup is ONE tab stop,
             * and arrow keys move within it. Thirteen voices behind thirteen tab
             * presses is how a keyboard user gives up. */
            card.tabIndex = (v.id === cur ? 0 : -1);

            var body = document.createElement('div');
            body.className = 'vp-card-body';

            var nm = document.createElement('div');
            nm.className = 'vp-card-name';
            nm.textContent = v.label + glyphFor(v.gender);
            body.appendChild(nm);

            var metaText = (typeof self.opts.rateText === 'function') ? self.opts.rateText(v) : '';
            if (metaText) {
                var meta = document.createElement('div');
                meta.className = 'vp-card-meta';
                meta.textContent = metaText;
                body.appendChild(meta);
            }
            card.appendChild(body);

            var play = document.createElement('button');
            play.type = 'button';
            play.className = 'vp-card-play';
            play.textContent = '▶';
            if (v.preview) {
                play.title = self.strings.preview || '';
                play.setAttribute('aria-label', (self.strings.preview || 'Preview') + ': ' + v.label);
                play.addEventListener('click', function (ev) {
                    ev.stopPropagation();
                    if (Engine.btn === play) { engineStop(); return; }
                    origStop();
                    enginePlay(v.preview, play);
                    self.tel('voice_preview', { voice: v.id, group: grp.key });
                });
            } else {
                /* A voice with no rendered sample keeps its button, disabled and
                 * explained. Hiding it would make the row look like a different
                 * KIND of voice instead of the same voice missing one file. */
                play.disabled = true;
                play.title = self.strings.no_preview || '';
                play.setAttribute('aria-label', self.strings.no_preview || 'No preview');
            }
            card.appendChild(play);

            card.addEventListener('click', function () { self.pick(v.id); });
            card.addEventListener('keydown', function (ev) {
                if (ev.key === ' ' || ev.key === 'Enter') { ev.preventDefault(); self.pick(v.id); return; }
                var step = (ev.key === 'ArrowRight' || ev.key === 'ArrowDown') ? 1
                    : (ev.key === 'ArrowLeft' || ev.key === 'ArrowUp') ? -1 : 0;
                if (!step) { return; }
                ev.preventDefault();
                var list = grp.voices;
                var at = list.map(function (x) { return x.id; }).indexOf(v.id);
                var next = list[(at + step + list.length) % list.length];
                if (next) { self.pick(next.id, true); }
            });

            self.gridEl.appendChild(card);
        });
    };

    /** Select a voice: the <select> moves first, the grid only reflects it. */
    Picker.prototype.pick = function (id, focus) {
        if (this.value === id) { return; }
        if (this.select) {
            this.select.value = id;
            this.select.dispatchEvent(new Event('change', { bubbles: true }));
        }
        this.render();
        if (focus) {
            var el = this.gridEl.querySelector('.vp-card.active');
            /* block:'nearest' only — never yank the page (CLAUDE.md, no auto-scroll). */
            if (el) { el.focus(); try { el.scrollIntoView({ block: 'nearest' }); } catch (e) { /* old engine */ } }
        }
        if (typeof this.opts.onChange === 'function') {
            try { this.opts.onChange(id); } catch (e) { /* a consumer must not break the grid */ }
        }
    };

    Object.defineProperty(Picker.prototype, 'value', {
        get: function () { return this.select ? this.select.value : this._value || ''; },
        set: function (id) { this.pick(id); }
    });

    Picker.prototype.stop = function () { engineStop(); origStop(); };

    Picker.prototype.destroy = function () {
        this.stop();
        this.root.innerHTML = '';
    };

    window.VoicePicker = {
        mount: function (root, opts) { return root ? new Picker(root, opts) : null; },
        /** Stop whatever is sounding — for `pagehide`, tab switches and run start. */
        stopAll: function () { engineStop(); origStop(); },
        /**
         * What the A/B player is doing, for anyone who needs to see it from
         * outside — a guard proving the comparison starts where the speaker
         * starts talking, not at the top of the file. That distinction is
         * invisible from the DOM (the element is never attached) and it broke
         * once already: routing this through __audioGate.play() rewound the
         * seek we had just made, because the gate resets currentTime for an
         * element that has one.
         */
        originalState: function () {
            return Orig.el ? { at: Orig.el.currentTime, playing: !Orig.el.paused } : null;
        }
    };

    /* Leaving the page with a preview still sounding is the same lingering-audio
     * complaint the Web Audio choice above exists to prevent. */
    window.addEventListener('pagehide', function () { window.VoicePicker.stopAll(); });
    document.addEventListener('visibilitychange', function () {
        if (document.hidden) { window.VoicePicker.stopAll(); }
    });
})();
